import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Info, Clock, Calendar } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface FormatInfoDialogProps {
  formatType: string;
  formatName: string;
  sport?: string;
  modality?: string;
  groupRules?: {
    setsPerMatch?: number;
    goldenPoint?: boolean;
    superTieBreak?: boolean;
    maxGamesPerSet?: number;
  };
  additionalRules?: {
    timing?: {
      warmupMinutes?: number;
      restMinutes?: number;
      sideChangeSeconds?: number;
    };
    conduct?: {
      toleranceMinutes?: number;
    };
    classification?: {
      firstPlaces?: number;
      rules?: string[];
    };
  };
  gameDays?: string[];
  startTime?: string;
  endTime?: string;
  isOpen: boolean;
  onClose: () => void;
}

const FormatInfoDialog = ({
  formatType,
  formatName,
  sport,
  modality,
  groupRules,
  additionalRules,
  gameDays,
  startTime,
  endTime,
  isOpen,
  onClose,
}: FormatInfoDialogProps) => {
  const { t } = useLanguage();

  const normalizeFormat = (ft: string) => ft?.toLowerCase().replace('_', '-');
  const isEscaleraFormat = (ft: string) => {
    const n = normalizeFormat(ft);
    return n === 'escalera-piramidal' || n === 'escalera';
  };

  const getNormalizedModality = () => (modality?.toLowerCase() || '');

  const isPadelSinglesEscalera = () => {
    if (!isEscaleraFormat(formatType)) return false;
    const normalizedSport = sport?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
    const isSingles = getNormalizedModality() === 'singles' || getNormalizedModality() === 'individual';
    return normalizedSport === 'padel' && isSingles;
  };

  const isDoublesEscalera = () => {
    if (!isEscaleraFormat(formatType)) return false;
    const mod = getNormalizedModality();
    return mod === 'doubles' || mod === 'dobles';
  };

  const getFormatInfo = () => {
    const normalizedFormat = formatType?.toLowerCase().replace('_', '-');
    
    switch (normalizedFormat) {
      case 'round-robin':
        return {
          title: t('formatInfo.roundRobin.title'),
          description: t('formatInfo.roundRobin.description'),
          features: [
            t('formatInfo.roundRobin.features.0'),
            t('formatInfo.roundRobin.features.1'),
            t('formatInfo.roundRobin.features.2'),
            t('formatInfo.roundRobin.features.3'),
          ],
          advantage: t('formatInfo.roundRobin.advantage'),
        };
      
      case 'sumatoria-juegos':
      case 'sumatoria_juegos':
        return {
          title: t('formatInfo.sumatoria.title'),
          description: t('formatInfo.sumatoria.description'),
          features: [
            t('formatInfo.sumatoria.features.0'),
            t('formatInfo.sumatoria.features.1'),
            t('formatInfo.sumatoria.features.2'),
            t('formatInfo.sumatoria.features.3'),
          ],
          advantage: t('formatInfo.sumatoria.advantage'),
        };
      
      case 'escalera-piramidal':
      case 'escalera_piramidal':
      case 'escalera':
        if (isDoublesEscalera()) {
          return {
            title: t('formatInfo.doublesEscalera.title'),
            description: t('formatInfo.doublesEscalera.description'),
            features: [
              t('formatInfo.doublesEscalera.features.0'),
              t('formatInfo.doublesEscalera.features.1'),
              t('formatInfo.doublesEscalera.features.2'),
              t('formatInfo.doublesEscalera.features.3'),
              t('formatInfo.doublesEscalera.features.4'),
              t('formatInfo.doublesEscalera.features.5'),
            ],
            advantage: t('formatInfo.doublesEscalera.advantage'),
            isDoublesEscalera: true,
          };
        }
        if (isPadelSinglesEscalera()) {
          return {
            title: t('formatInfo.padelSingles.title'),
            description: t('formatInfo.padelSingles.description'),
            features: [
              t('formatInfo.padelSingles.features.0'),
              t('formatInfo.padelSingles.features.1'),
              t('formatInfo.padelSingles.features.2'),
              t('formatInfo.padelSingles.features.3'),
              t('formatInfo.padelSingles.features.4'),
              t('formatInfo.padelSingles.features.5'),
            ],
            advantage: t('formatInfo.padelSingles.advantage'),
            isPadelSingles: true,
          };
        }
        return {
          title: t('formatInfo.escalera.title'),
          description: t('formatInfo.escalera.description'),
          features: [
            t('formatInfo.escalera.features.0'),
            t('formatInfo.escalera.features.1'),
            t('formatInfo.escalera.features.2'),
            t('formatInfo.escalera.features.3'),
          ],
          advantage: t('formatInfo.escalera.advantage'),
        };
      
      case 'eliminacion-directa':
      case 'eliminacion_directa':
      case 'elimination':
        return {
          title: t('formatInfo.elimination.title'),
          description: t('formatInfo.elimination.description'),
          features: [
            t('formatInfo.elimination.features.0'),
            t('formatInfo.elimination.features.1'),
            t('formatInfo.elimination.features.2'),
            t('formatInfo.elimination.features.3'),
          ],
          advantage: t('formatInfo.elimination.advantage'),
        };
      
      default:
        return {
          title: formatName,
          description: t('formatInfo.default.description'),
          features: [
            t('formatInfo.default.features.0'),
            t('formatInfo.default.features.1'),
          ],
          advantage: "",
        };
    }
  };

  const formatInfo = getFormatInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Info className="h-6 w-6 text-primary" />
            {formatInfo.title}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {formatInfo.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Features */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              {t('formatInfo.features')}
            </h3>
            <ul className="space-y-2">
              {formatInfo.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary mt-1">•</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Game Rules if available */}
          {groupRules && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">⚙️ {t('formatInfo.matchConfig')}</h3>
              <ul className="space-y-2 text-sm">
                {groupRules.setsPerMatch && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.setsPerMatch')} <strong>{groupRules.setsPerMatch}</strong></span>
                  </li>
                )}
                {groupRules.maxGamesPerSet && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.gamesPerSet')} <strong>{groupRules.maxGamesPerSet}</strong></span>
                  </li>
                )}
                {groupRules.goldenPoint !== undefined && (
                  <li className="flex items-start gap-2">
                    <span className={groupRules.goldenPoint ? "text-amber-600 mt-1" : "text-muted-foreground mt-1"}>•</span>
                    <span className={groupRules.goldenPoint ? "text-amber-600 font-medium" : ""}>
                      {groupRules.goldenPoint ? `⚡ ${t('formatInfo.goldenPointOn')}` : t('formatInfo.goldenPointOff')}
                    </span>
                  </li>
                )}
                {groupRules.superTieBreak !== undefined && (
                  <li className="flex items-start gap-2">
                    <span className={groupRules.superTieBreak ? "text-purple-600 mt-1" : "text-muted-foreground mt-1"}>•</span>
                    <span className={groupRules.superTieBreak ? "text-purple-600 font-medium" : ""}>
                      {groupRules.superTieBreak ? `🎯 ${t('formatInfo.superTiebreakOn')}` : t('formatInfo.superTiebreakOff')}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Timing Rules */}
          {additionalRules?.timing && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                {t('formatInfo.gameTiming')}
              </h3>
              <ul className="space-y-2 text-sm">
                {additionalRules.timing.warmupMinutes && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.warmup')} <strong>{additionalRules.timing.warmupMinutes} {t('formatInfo.minutes')}</strong></span>
                  </li>
                )}
                {additionalRules.timing.restMinutes && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.restBetweenSets')} <strong>{additionalRules.timing.restMinutes} {t('formatInfo.minutes')}</strong></span>
                  </li>
                )}
                {additionalRules.timing.sideChangeSeconds && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.sideChange')} <strong>{additionalRules.timing.sideChangeSeconds} {t('formatInfo.seconds')}</strong></span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Schedule and Days */}
          {(gameDays || startTime || endTime) && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600" />
                {t('formatInfo.scheduleAndDays')}
              </h3>
              <ul className="space-y-2 text-sm">
                {gameDays && gameDays.length > 0 && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.gameDays')} <strong>{gameDays.join(', ')}</strong></span>
                  </li>
                )}
                {(startTime || endTime) && (
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>{t('formatInfo.schedule')} <strong>{startTime?.slice(0,5) || '--:--'} {t('formatInfo.to')} {endTime?.slice(0,5) || '--:--'}</strong></span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Special section for Doubles Escalera (Rey de la Cancha) */}
          {isDoublesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 space-y-3">
                <h3 className="font-semibold text-lg text-blue-700 flex items-center gap-2">
                  👑 {t('formatInfo.doublesEscalera.systemTitle')}
                </h3>
                <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: t('formatInfo.doublesEscalera.systemDesc') }} />
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    ⬆️ {t('formatInfo.doublesEscalera.winnerUp')}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    ⬇️ {t('formatInfo.doublesEscalera.loserDown')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Special section for Pádel Singles Escalera */}
          {isPadelSinglesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200 space-y-3">
                <h3 className="font-semibold text-lg text-orange-700 flex items-center gap-2">
                  🔄 {t('formatInfo.padelSingles.systemTitle')}
                </h3>
                <p className="text-sm text-orange-800" dangerouslySetInnerHTML={{ __html: t('formatInfo.padelSingles.systemDesc') }} />
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    ⬆️ {t('formatInfo.padelSingles.bestUp')}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    ⬇️ {t('formatInfo.padelSingles.worstDown')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Special section for Generic Escalera Piramidal */}
          {(formatType === 'escalera-piramidal' || formatType === 'escalera_piramidal' || formatType === 'escalera') && !isPadelSinglesEscalera() && (
            <div className="border-t pt-4">
              <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                <h3 className="font-semibold text-lg text-purple-700 flex items-center gap-2">
                  🪜 {t('formatInfo.escalera.specialFeatures')}
                </h3>
                <ul className="space-y-2 text-sm">
                  {gameDays && gameDays.length > 0 ? (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>{t('formatInfo.escalera.scheduledOn')} <strong>{gameDays.join(', ')}</strong></span>
                    </li>
                  ) : (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">✅</span>
                      <span><strong>{t('formatInfo.escalera.dateFlexibility')}</strong> {t('formatInfo.escalera.dateFlexibilityDesc')}</span>
                    </li>
                  )}
                  {additionalRules?.conduct?.toleranceMinutes && (
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <span>{t('formatInfo.arrivalTolerance')} <strong>{additionalRules.conduct.toleranceMinutes} {t('formatInfo.minutes')}</strong></span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">📞</span>
                    <span><strong>{t('formatInfo.escalera.directCoordination')}</strong> {t('formatInfo.escalera.directCoordinationDesc')}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Conduct Rules (only if not Escalera) */}
          {additionalRules?.conduct?.toleranceMinutes && 
           formatType !== 'escalera-piramidal' && 
           formatType !== 'escalera_piramidal' && 
           formatType !== 'escalera' && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                ⚠️ {t('formatInfo.conduct')}
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{t('formatInfo.arrivalTolerance')} <strong>{additionalRules.conduct.toleranceMinutes} {t('formatInfo.minutes')}</strong></span>
                </li>
              </ul>
            </div>
          )}

          {/* Advantage */}
          {formatInfo.advantage && (
            <div className="border-t pt-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground">
                  ✨ {formatInfo.advantage}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormatInfoDialog;
